import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Box, Typography, TextField, InputAdornment, Button, Chip, Menu, MenuItem
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { db } from '../../utils/firebase';
import { collection, query, where, orderBy, onSnapshot } from "firebase/firestore";
import styles from "./AdminMain.module.css";

interface Report {
  id: string;
  patientId: string;
  checkTypes: string[];
  timestamp: any;
  dateString: string;
}

const AdminMain: React.FC = () => {
  const { hospitalId } = useParams();
  const navigate = useNavigate();
  const [reports, setReports] = useState<Report[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [searchMode, setSearchMode] = useState<"ID" | "Date">("ID");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const whiteBoxRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleMenuClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = (mode?: "ID" | "Date") => {
    if (mode) {
      setSearchMode(mode);
      setSearchTerm(""); // 모드 변경 시 입력값 초기화
    }
    setAnchorEl(null);
  };

  const toggleFilter = (cat: string) => {
    setActiveFilters((prev) =>
      prev.includes(cat) 
        ? prev.filter((f) => f !== cat) // 이미 선택되어 있으면 제거
        : [...prev, cat]               // 선택 안 되어 있으면 추가
    );
  };

  useEffect(() => {
    const boxElement = whiteBoxRef.current;
    if (!boxElement) return;

    const handleScroll = () => {
      // 스크롤 시 숨김 클래스 제거
      boxElement.classList.remove(styles.scrollHidden);
      
      // 기존 타이머 클리어 후 400ms 뒤에 다시 숨김 클래스 추가
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        boxElement.classList.add(styles.scrollHidden);
      }, 400); 
    };

    boxElement.addEventListener('scroll', handleScroll);
    boxElement.classList.add(styles.scrollHidden); // 초기 상태는 숨김

    return () => {
      boxElement.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [reports]);

  // 1. Firebase 실시간 데이터 호출
  useEffect(() => {
    const reportRef = collection(db, "reports");
    const searchTargetId = hospitalId || "default";
    const q = query(
      reportRef, 
      where("hospitalId", "==", searchTargetId), 
      orderBy("timestamp", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => {
        const docData = doc.data();
        const date = docData.timestamp?.toDate() || new Date();
        return {
          id: doc.id,
          ...docData,
          dateString: `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
        } as Report;
      });
      setReports(data);
    });

    return () => unsubscribe();
  }, [hospitalId]);

  // 2. 검색 및 필터링 로직
  const filteredReports = reports.filter(r => {
    const term = searchTerm.toLowerCase();

    const matchesSearch = searchMode === "ID" 
      ? r.id.toLowerCase().includes(searchTerm.trim().toLowerCase())
      : r.dateString.replace(/\./g, '-').includes(searchTerm);
    
    const matchesFilter = activeFilters.length > 0 
      ? activeFilters.every(filter => r.checkTypes.includes(filter))
      : true;
    return matchesSearch && matchesFilter;
  });

  // 3. 날짜별 그룹화
  const groupedReports = filteredReports.reduce((acc, report) => {
    if (!acc[report.dateString]) acc[report.dateString] = [];
    acc[report.dateString].push(report);
    return acc;
  }, {} as Record<string, Report[]>);

  const categories = ["Stroke", "Cognitive", "Headache"];

  const renderHighlightedText = (text: string, highlight: string) => {
    if (searchMode !== 'ID' || !highlight.trim()) {
       return <span>{text}</span>;
    }

    const escapeRegExp = (string: string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    const regex = new RegExp(`(${escapeRegExp(highlight.trim())})`, 'gi');
    
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          part.toLowerCase() === highlight.trim().toLowerCase() ? (
            <mark key={index} className={styles.highlight}>{part}</mark>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </>
    );
  }

  return (
    <div className={styles.adminContainer}>
      <div className={styles.whiteBox} ref={whiteBoxRef}>
        
        {/* 상단 검색 및 필터 섹션 */}
        <div className={styles.searchSection}>
          <TextField
            className={styles.searchField}
            variant="outlined"
            size="small"
            type={searchMode === "Date" ? "date" : "text"}
            placeholder={searchMode === "ID" ? "ID 입력" : ""}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                  <Box 
                    onClick={handleMenuClick} 
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ml: 1, mr: 1 }}
                  >
                    <ArrowDropDownIcon sx={{ color: '#aaa', fontSize: 20 }} />
                    <Typography variant="body2" sx={{ color: '#888', fontWeight: 'bold' }}>
                      {searchMode}
                    </Typography>
                  </Box>
                </InputAdornment>
              ),
            }}
            sx={{ 
              "& .MuiOutlinedInput-root": { borderRadius: "12px" },
              width: { xs: '100%', sm: '450px' }
            }}
          />
          
          <Menu anchorEl={anchorEl} open={openMenu} onClose={() => handleMenuClose()}>
            <MenuItem onClick={() => handleMenuClose("ID")}>ID로 검색</MenuItem>
            <MenuItem onClick={() => handleMenuClose("Date")}>날짜로 검색</MenuItem>
          </Menu>

          <div className={styles.filterGroup}>
            {categories.map(cat => (
              <Button
                key={cat}
                variant={activeFilters.includes(cat) ? "contained" : "outlined"}
                size="small"
                onClick={() => toggleFilter(cat)}
                sx={{ borderRadius: "20px", textTransform: "none" }}
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>

        {/* 날짜별 리스트 렌더링 */}
        {Object.keys(groupedReports).length > 0 ? (
          Object.keys(groupedReports).sort((a, b) => b.localeCompare(a)).map((date) => (
            <div key={date}>
              <h3 className={styles.dateHeader}>{date}</h3>
              {groupedReports[date].map((report) => (
                <div 
                  key={report.id} 
                  className={styles.card}
                  onClick={() => navigate(hospitalId ? `/${hospitalId}/admin/report/${report.id}` : `/admin/report/${report.id}`)}
                >
                  <div className={styles.cardTitle}>
                    ID : {renderHighlightedText(report.id, searchTerm)}
                  </div>
                  <div className={styles.chipGroup}>
                    {report.checkTypes?.map((type) => (
                      <Chip 
                        key={type} 
                        label={type} 
                        size="small" 
                        sx={{ bgcolor: "#eee", fontWeight: "600", color: "#666" }} 
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))
        ) : (
          <Typography sx={{ mt: 4, color: "#999" }}>검색 결과가 없습니다.</Typography>
        )}
      </div>
    </div>
  );
};

export default AdminMain;